import cx from 'classnames';
import {Fragment, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import * as uuid from 'uuid/v4';

import {Icon, Input, Select, TextArea, Toggle} from 'components';
import {CustomField, SessionScreenshot} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {
    PRE_SESSION_FLOW_SUPPORTED_ASYNC_REQUESTS,
    PreSessionFormField,
    PreSessionFormFieldInputType,
    PreSessionStep,
    PreSessionStepType,
} from 'types';
import {buildTranslationKey} from 'utils/transform';

import {FormFieldsEditor} from './FormFieldsEditor';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.PRE_SESSION_FLOW;
const ASYNC_TYPE_LABEL_KEYS = TRANSLATION_KEYS.COMMON.SESSION.ASYNC_REQUEST.TYPE;

type Props = {
    step: PreSessionStep;
    index: number;
    total: number;
    isLast: boolean;
    disabled: boolean;
    userInfoFields: CustomField[];
    screenshots: SessionScreenshot[];
    onChange: (next: PreSessionStep) => void;
    onRemove: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
};

const buildFormSingleField = (): PreSessionFormField => ({
    id: uuid(),
    label: '',
    inputType: PreSessionFormFieldInputType.Text,
    required: true,
});

export const PreSessionStepRow: React.FC<Props> = ({
    step, index, total, isLast, disabled, userInfoFields, screenshots, onChange, onRemove, onMoveUp, onMoveDown,
}: Props) => {
    const {t} = useTranslation();

    const hasScreenshots = screenshots.length > 0;

    const typeOptions = useMemo(() => [
        {id: PreSessionStepType.Form, name: t(T_KEYS.STEP.TYPE_FIELD_VALUE)},
        {id: PreSessionStepType.AsyncRequest, name: t(T_KEYS.STEP.TYPE_ASYNC_REQUEST)},
        {
            id: PreSessionStepType.Screenshot,
            name: hasScreenshots
                ? t(T_KEYS.STEP.TYPE_SCREENSHOT)
                : `${t(T_KEYS.STEP.TYPE_SCREENSHOT)} (${t(T_KEYS.STEP.SCREENSHOT_EMPTY_HINT_SHORT)})`,
            isDisabled: !hasScreenshots && step.type !== PreSessionStepType.Screenshot,
        },
    ], [t, hasScreenshots, step.type]);

    const asyncRequestOptions = useMemo(
        () => PRE_SESSION_FLOW_SUPPORTED_ASYNC_REQUESTS.map((id) => {
            const key = ASYNC_TYPE_LABEL_KEYS[buildTranslationKey(id)];
            return {id, name: key ? t(key) : id};
        }),
        [t]
    );

    const handleTypeChange = useCallback(({id}: {id: PreSessionStepType}) => {
        const base: PreSessionStep = {
            id: step.id,
            name: step.name,
            type: id,
            required: step.required,
            clientPrompt: step.clientPrompt,
        };
        if (id === PreSessionStepType.AsyncRequest) {
            base.asyncRequest = {typeName: PRE_SESSION_FLOW_SUPPORTED_ASYNC_REQUESTS[0]};
        } else if (id === PreSessionStepType.Screenshot) {
            base.screenshot = {name: ''};
        } else if (id === PreSessionStepType.Form) {
            base.form = {fields: [buildFormSingleField()]};
        }
        onChange(base);
    }, [step, onChange]);

    const handleRequiredChange = useCallback((required: boolean) => {
        onChange({...step, required});
    }, [step, onChange]);

    const handleNameChange = useCallback((name: string) => {
        onChange({...step, name});
    }, [step, onChange]);

    const handleClientInstructionsChange = useCallback((clientPrompt: string) => {
        onChange({...step, clientPrompt});
    }, [step, onChange]);

    const handleAsyncTypeChange = useCallback(({id}: {id: typeof PRE_SESSION_FLOW_SUPPORTED_ASYNC_REQUESTS[number]}) => {
        onChange({...step, asyncRequest: {typeName: id}});
    }, [step, onChange]);

    const handleScreenshotNameChange = useCallback((name: string) => {
        onChange({...step, screenshot: {name}});
    }, [step, onChange]);

    const handleFormFieldsChange = useCallback((fields: PreSessionFormField[]) => {
        onChange({...step, form: {fields}});
    }, [step, onChange]);

    const screenshotOptions = useMemo(
        () => screenshots.map((s) => ({id: s.name, name: s.name})),
        [screenshots],
    );

    const renderValueEditor = () => {
        switch (step.type) {
            case PreSessionStepType.AsyncRequest: {
                const value = asyncRequestOptions.find((o) => o.id === step.asyncRequest?.typeName) ?? asyncRequestOptions[0];
                return (
                    <Select
                        placeholder={t(T_KEYS.STEP.ASYNC_REQUEST_LABEL)}
                        options={asyncRequestOptions}
                        value={value}
                        disabled={disabled}
                        onChange={handleAsyncTypeChange}
                    />
                );
            }
            case PreSessionStepType.Screenshot: {
                const value = screenshotOptions.find((o) => o.id === step.screenshot?.name);
                if (screenshotOptions.length === 0) {
                    return (
                        <Select
                            placeholder={t(T_KEYS.STEP.SCREENSHOT_EMPTY_HINT)}
                            options={[]}
                            value={null}
                            disabled
                            onChange={() => undefined}
                        />
                    );
                }
                return (
                    <Select
                        placeholder={t(T_KEYS.STEP.SCREENSHOT_NAME_LABEL)}
                        options={screenshotOptions}
                        value={value}
                        disabled={disabled}
                        onChange={({id}: {id: string}) => handleScreenshotNameChange(id)}
                    />
                );
            }
            case PreSessionStepType.Form: {
                return (
                    <FormFieldsEditor
                        fields={step.form?.fields ?? []}
                        userInfoFields={userInfoFields}
                        disabled={disabled}
                        onChange={handleFormFieldsChange}
                    />
                );
            }
            default:
                return null;
        }
    };

    const typeValue = useMemo(() => typeOptions.find((o) => o.id === step.type), [typeOptions, step.type]);

    return (
        <div className={cx('tw-py-4 tw-flex tw-flex-col tw-gap-3', {'tw-border-b tw-border-default': !isLast})}>
            <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2'>
                <span
                    className='tw-order-1 tw-w-7 tw-h-7 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center
                        tw-rounded-full tw-bg-primary-lighten tw-text-primary tw-ts-h5-bold'
                >
                    {index + 1}
                </span>
                <div className='tw-order-2 tw-flex-1 tw-min-w-0'>
                    <Input
                        label={t(T_KEYS.STEP.NAME_LABEL)}
                        value={step.name ?? ''}
                        required
                        disabled={disabled}
                        onChange={handleNameChange}
                    />
                </div>
                <div className='tw-order-4 sm:tw-order-3 tw-w-full sm:tw-w-auto tw-flex-shrink-0 tw-pl-10 sm:tw-pl-0'>
                    <Toggle
                        name={`step-${step.id}-required`}
                        checked={!!step.required}
                        disabled={disabled}
                        label={t(T_KEYS.STEP.REQUIRED)}
                        labelPlacement='right'
                        size='small'
                        onChange={handleRequiredChange}
                    />
                </div>
                <div className='tw-order-3 sm:tw-order-4 tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0'>
                    <Icon
                        name='arrow-up'
                        size='md'
                        button
                        color='primary'
                        margin={false}
                        disabled={disabled || index === 0}
                        title={t(T_KEYS.STEP.MOVE_UP)}
                        onClick={onMoveUp}
                    />
                    <Icon
                        name='arrow-down'
                        size='md'
                        button
                        color='primary'
                        margin={false}
                        disabled={disabled || index === total - 1}
                        title={t(T_KEYS.STEP.MOVE_DOWN)}
                        onClick={onMoveDown}
                    />
                    <Icon
                        name='delete'
                        size='md'
                        button
                        color='alert'
                        margin={false}
                        disabled={disabled}
                        title={t(T_KEYS.STEP.REMOVE)}
                        onClick={onRemove}
                    />
                </div>
            </div>

            <div className='tw-pl-10 tw-flex tw-flex-col tw-gap-3'>
                {step.type === PreSessionStepType.Form ? (
                    <Fragment>
                        <div className='tw-w-44'>
                            <Select
                                placeholder={t(T_KEYS.STEP.TYPE_LABEL)}
                                options={typeOptions}
                                value={typeValue}
                                disabled={disabled}
                                onChange={handleTypeChange}
                            />
                        </div>
                        <div className='tw-w-full'>
                            {renderValueEditor()}
                        </div>
                    </Fragment>
                ) : (
                    <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
                        <div className='tw-w-44 tw-flex-shrink-0'>
                            <Select
                                placeholder={t(T_KEYS.STEP.TYPE_LABEL)}
                                options={typeOptions}
                                value={typeValue}
                                disabled={disabled}
                                onChange={handleTypeChange}
                            />
                        </div>
                        <div className='tw-flex-1 tw-min-w-[220px]'>
                            {renderValueEditor()}
                        </div>
                    </div>
                )}
            </div>

            <div className='tw-pl-10'>
                <TextArea
                    label={t(T_KEYS.STEP.CLIENT_INSTRUCTIONS_LABEL)}
                    value={step.clientPrompt ?? ''}
                    rows={3}
                    required
                    disabled={disabled}
                    onChange={handleClientInstructionsChange}
                />
            </div>
        </div>
    );
};
